import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);
dayjs.extend(utc);

export const formatDate = (
  date: string | number | dayjs.Dayjs | Date | null | undefined
) => {
  return dayjs(date).format("MM-D-YY");
};

export const formatShortDate = (
  date: string | number | dayjs.Dayjs | Date | null | undefined
) => {
  return dayjs.utc(date).format("M-D-YY");
};

export const formatReverseDate = (
  date: string | number | dayjs.Dayjs | Date | null | undefined
) => {
  return dayjs(date).format("YYYY-MM-DD");
};

export const formatDateWithFullYear = (
  date: string | number | dayjs.Dayjs | Date | null | undefined
) => {
  return dayjs(date).format("MM-D-YYYY");
};

export const newSameDay = (
  date: string | number | dayjs.Dayjs | Date | null | undefined
) => {
  return dayjs().isSame(date, "day");
};

export const formatDateAndTime = (
  date: string | number | dayjs.Dayjs | Date | null | undefined
) => {
  return dayjs(date).format("M-D-YY, h:MM A");
};

export const getDateToday = () => {
  return dayjs().startOf("day").format("YYYY-MM-DD");
};

export const getTimestamp = (mills: any) => {
  return dayjs(mills).toISOString();
};

export const convertMillisecondsToStringDate = (
  milliseconds: string | number | Date
) => {
  return new Date(milliseconds).toISOString();
};

export const getLastWeekDate = () => {
  // Return string format for date-range widget
  // @ts-ignore
  return dayjs().startOf("day").subtract(1, "week")["$d"];
};

export const formatReverseDateUtc = (
  timestamp: string | number | dayjs.Dayjs | Date | null | undefined
) => {
  const date = dayjs.utc(timestamp);
  return dayjs.utc(date).format("YYYY-MM-DD");
};

export const convertTo12HrTime = (timestamp: string) => {
  // Set an irrelevant default date + timestamp to convert to 12 hr time
  return timestamp ? dayjs("1/1/1 " + timestamp).format("hh:mm a") : null;
};

export const convertTo12HrTimeWithDate = (
  datetime: string | number | dayjs.Dayjs | Date | null | undefined
) => {
  // Set an irrelevant default date + timestamp to convert to 12 hr time
  return datetime ? dayjs(datetime).format("h:mm a") : null;
};

export const convertTo24HrTime = (timestamp: string) => {
  return timestamp ? dayjs("1/1/1 " + timestamp).format("HH:mm:ss") : null;
};

// Return None for UI display purposes when a timestamp is null or undefined
export const formatDateWithFullYearUtc = (
  timestamp: string | number | dayjs.Dayjs | Date | null | undefined
) => {
  if (timestamp === null || timestamp === undefined || timestamp === "") {
    return "None";
  }
  const date = dayjs.utc(timestamp);
  return dayjs.utc(date).format("MM-D-YYYY");
};

export const formatReverseDateFullYear = (
  date: string | number | dayjs.Dayjs | Date | null | undefined
) => {
  if (date === null || date === undefined || date === "") {
    return "None";
  }
  return dayjs(date).format("YYYY-MM-DD");
};

export const convertToISOString = (
  date: string | number | dayjs.Dayjs | Date | null | undefined
) => {
  return dayjs(date).toISOString();
};

export const abbreviateServicingDays = (servicingDaysString: string | null) => {
  if (servicingDaysString === null) {
    return null;
  }
  let servicingDaysArray = servicingDaysString.split("/");
  let abbreviatedDays = [];
  if (servicingDaysArray.length === 0) {
    return "N/A";
  }
  for (let i = 0; i < servicingDaysArray.length; i++) {
    if (
      servicingDaysArray[i].slice(0, 2) === "Th" ||
      servicingDaysArray[i].slice(0, 2) === "Su"
    ) {
      abbreviatedDays.push(servicingDaysArray[i].slice(0, 2));
    } else {
      abbreviatedDays.push(servicingDaysArray[i].slice(0, 1));
    }
  }
  return abbreviatedDays.join("/");
};

/**
 * Add a numerical amount of days to the current passed in date.
 *
 * date argument must be of valid date type (Date) or a string representation of a date
 * otherwise the catch statement will execute.
 *
 * Javascript is hilariously inconsistent with date objects. It will attempt to parse a date
 * as the local time zone that you're in so if the time part of the date is 00.00... it will subtract
 * x amount of hours (EST -4:00/-5:00) from it. This will most likely give you the day before.
 *
 * A solution is to parse a string representation of a date with the following format YYYY-MM-D and
 * then reconstruct the date with the newly parsed string date.
 *
 * @param date Date that will have days incremented
 * @param days amount of days to increment date by
 * @returns {*}
 */
export const addDaysToStringDate = (date: any, days: any) => {
  let newDate = date;
  try {
    if (!(date instanceof Date)) {
      if (typeof date === "string") {
        date = date.substring(0, 10);
      }
      // @ts-ignore
      newDate = new Date(date);
    }
    // @ts-ignore
    if ("setDate" in newDate) {
      newDate.setDate(newDate.getDate() + days);
    }
  } catch (err) {
    throw Error("addDaysToDate invalid operation - " + err);
  }
  if (isNaN(<any>newDate)) {
    throw Error("Invalid Date Received");
  }
  return newDate;
};

export const isOverlappingDateRange = (
  newStartDate: any,
  newEndDate: any,
  startDate: any,
  endDate: any
) => {
  return (
    dayjs(newStartDate).isBetween(startDate, endDate, null, "[]") ||
    (newEndDate != null &&
      dayjs(newEndDate).isBetween(startDate, endDate, null, "[]"))
  );
};

/* Get day of week 6 days from startDate
 * @param startDate {string}
 * @return endDate {string}
 */
export const getEndDay = (startDate: string) => {
  const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const startDayIndex = daysOfWeek.indexOf(startDate);
  return daysOfWeek[(startDayIndex + 6) % 7];
};

/**
 * Checks if the given ISO date is in the past
 * @param date - Date in ISO format (YYYY-MM-DD)
 */
export function isPastDate(
  date: string | number | dayjs.Dayjs | Date | null | undefined
) {
  if (date === null || date === undefined) {
    return false;
  }
  const today = dayjs();
  return dayjs(date).isBefore(today);
}
